import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './RunProfileDetail.css';

const RunProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  usePageTitle(profile ? profile.name : 'Run Profile');
  const [executions, setExecutions] = useState([]);
  const [availableItems, setAvailableItems] = useState({ pipelines: [], flows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState('flow');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchProfile();
    fetchExecutions();
    fetchAvailableItems();
  }, [id]);

  const fetchProfile = async () => {
    try {
      const response = await axiosInstance.get(`/etl/run-profiles/${id}/`);
      setProfile(response.data);
      setEditData({
        name: response.data.name,
        description: response.data.description,
        status: response.data.status,
        stop_on_failure: response.data.stop_on_failure,
        parallel_execution: response.data.parallel_execution,
      });
      setLoading(false);
    } catch (err) {
      setError('Failed to load profile');
      setLoading(false);
    }
  };

  const fetchExecutions = async () => {
    try {
      const response = await axiosInstance.get(`/etl/run-profiles/${id}/executions/`);
      setExecutions(response.data);
    } catch (err) {
      console.error('Failed to load executions:', err);
    }
  };

  const fetchAvailableItems = async () => {
    try {
      const response = await axiosInstance.get('/etl/run-profiles/available_items/');
      setAvailableItems(response.data);
    } catch (err) {
      console.error('Failed to load available items:', err);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const response = await axiosInstance.patch(`/etl/run-profiles/${id}/`, editData);
      setProfile(response.data);
      setIsEditing(false);
    } catch (err) {
      setError(err.response?.data?.name?.[0] || 'Failed to update profile');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!selectedItemId) return;

    try {
      await axiosInstance.post(`/etl/run-profiles/${id}/add_item/`, {
        item_type: selectedItemType,
        item_id: parseInt(selectedItemId),
      });
      fetchProfile();
      setShowAddModal(false);
      setSelectedItemId('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add item');
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!confirm('Remove this item from the profile?')) return;
    try {
      await axiosInstance.post(`/etl/run-profiles/${id}/remove_item/`, { item_id: itemId });
      fetchProfile();
    } catch (err) {
      setError('Failed to remove item');
    }
  };

  const handleMoveItem = async (itemId, direction) => {
    const items = [...profile.items];
    const index = items.findIndex(item => item.id === itemId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    // Swap items
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    const newOrder = items.map(item => item.id);

    try {
      await axiosInstance.post(`/etl/run-profiles/${id}/reorder_items/`, { item_order: newOrder });
      fetchProfile();
    } catch (err) {
      setError('Failed to reorder items');
    }
  };

  const handleToggleItem = async (item) => {
    try {
      // Use PATCH to update the item's enabled status
      await axiosInstance.patch(`/etl/run-profiles/${id}/`, {
        items: profile.items.map(i =>
          i.id === item.id ? { ...i, is_enabled: !i.is_enabled } : i
        )
      });
      fetchProfile();
    } catch (err) {
      // Fallback: refetch profile
      fetchProfile();
    }
  };

  const handleTriggerProfile = async () => {
    try {
      await axiosInstance.post(`/etl/run-profiles/${id}/trigger/`);
      fetchExecutions();
      fetchProfile();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to trigger profile');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const getStatusBadge = (status) => {
    const classes = {
      pending: 'badge-warning',
      running: 'badge-info',
      completed: 'badge-success',
      partial: 'badge-warning',
      failed: 'badge-error',
      cancelled: 'badge-secondary',
      active: 'badge-success',
      inactive: 'badge-warning',
      archived: 'badge-secondary',
    };
    return classes[status] || 'badge-secondary';
  };

  const getItemTypeBadge = (type) => {
    return type === 'pipeline' ? 'badge-pipeline' : 'badge-flow';
  };

  // Filter out items already in the profile
  const getFilteredAvailableItems = () => {
    const existingFlowIds = profile?.items?.filter(i => i.item_type === 'flow').map(i => i.flow) || [];
    const existingPipelineIds = profile?.items?.filter(i => i.item_type === 'pipeline').map(i => i.pipeline) || [];

    return {
      flows: availableItems.flows.filter(f => !existingFlowIds.includes(f.id)),
      pipelines: availableItems.pipelines.filter(p => !existingPipelineIds.includes(p.id)),
    };
  };

  if (loading) {
    return (
      <div className="profile-detail-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-detail-container">
        <div className="error">Profile not found</div>
      </div>
    );
  }

  const filteredItems = getFilteredAvailableItems();

  return (
    <div className="profile-detail-container">
      {/* Header */}
      <div className="detail-header">
        <button onClick={() => navigate('/run-profiles')} className="btn-back">
          ← Back to Profiles
        </button>
        <div className="header-actions">
          <button onClick={handleTriggerProfile} className="btn-primary btn-run">
            ▶ Run Profile
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)} className="error-dismiss">×</button>
        </div>
      )}

      {/* Profile Info */}
      <div className="profile-info-card">
        {isEditing ? (
          <div className="edit-form">
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="form-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={editData.stop_on_failure}
                  onChange={(e) => setEditData({ ...editData, stop_on_failure: e.target.checked })}
                />
                Stop on Failure
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={editData.parallel_execution}
                  onChange={(e) => setEditData({ ...editData, parallel_execution: e.target.checked })}
                />
                Parallel Execution
              </label>
            </div>
            <div className="edit-actions">
              <button onClick={() => setIsEditing(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleUpdateProfile} className="btn-primary">Save Changes</button>
            </div>
          </div>
        ) : (
          <>
            <div className="profile-info-header">
              <div>
                <h1>{profile.name}</h1>
                <span className={`badge ${getStatusBadge(profile.status)}`}>{profile.status}</span>
              </div>
              <button onClick={() => setIsEditing(true)} className="btn-secondary">Edit</button>
            </div>
            {profile.description && <p className="profile-description">{profile.description}</p>}
            <div className="profile-stats-row">
              <div className="stat-item">
                <span className="stat-label">Total Runs</span>
                <span className="stat-value">{profile.total_runs}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Success Rate</span>
                <span className="stat-value">{profile.success_rate?.toFixed(1) || 0}%</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Duration</span>
                <span className="stat-value">{formatDuration(profile.avg_duration_seconds)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Last Run</span>
                <span className="stat-value">{formatDate(profile.last_run_at)}</span>
              </div>
            </div>
            <div className="profile-options-row">
              {profile.stop_on_failure && <span className="option-tag">Stop on Failure</span>}
              {profile.parallel_execution && <span className="option-tag">Parallel Execution</span>}
            </div>
          </>
        )}
      </div>

      {/* Items Section */}
      <div className="items-section">
        <div className="section-header">
          <h2>Profile Items ({profile.items?.length || 0})</h2>
          <button onClick={() => setShowAddModal(true)} className="btn-secondary">
            + Add Item
          </button>
        </div>

        {profile.items?.length === 0 ? (
          <div className="empty-items">
            <p>No items in this profile yet. Add flows or pipelines to run together.</p>
            <button onClick={() => setShowAddModal(true)} className="btn-primary">
              Add First Item
            </button>
          </div>
        ) : (
          <div className="items-list">
            {profile.items?.map((item, index) => (
              <div key={item.id} className={`item-row ${!item.is_enabled ? 'disabled' : ''}`}>
                <div className="item-order">
                  <button
                    onClick={() => handleMoveItem(item.id, 'up')}
                    disabled={index === 0}
                    className="btn-move"
                  >
                    ↑
                  </button>
                  <span className="order-number">{index + 1}</span>
                  <button
                    onClick={() => handleMoveItem(item.id, 'down')}
                    disabled={index === profile.items.length - 1}
                    className="btn-move"
                  >
                    ↓
                  </button>
                </div>
                <div className="item-info">
                  <span className={`badge ${getItemTypeBadge(item.item_type)}`}>
                    {item.item_type}
                  </span>
                  <span className="item-name">{item.flow_name || item.pipeline_name}</span>
                </div>
                <div className="item-meta">
                  {item.continue_on_failure && (
                    <span className="meta-tag">Continue on Failure</span>
                  )}
                </div>
                <div className="item-actions">
                  <button
                    onClick={() => handleRemoveItem(item.id)}
                    className="btn-icon btn-danger"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Executions Section */}
      <div className="executions-section">
        <div className="section-header">
          <h2>Recent Executions</h2>
        </div>
        {executions.length === 0 ? (
          <div className="empty-executions">
            <p>No executions yet. Run this profile to see execution history.</p>
          </div>
        ) : (
          <div className="executions-table">
            <table>
              <thead>
                <tr>
                  <th>Run ID</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Progress</th>
                  <th>Triggered By</th>
                </tr>
              </thead>
              <tbody>
                {executions.map((exec) => (
                  <tr key={exec.id}>
                    <td><code>{exec.run_id}</code></td>
                    <td>
                      <span className={`badge ${getStatusBadge(exec.status)}`}>
                        {exec.status}
                      </span>
                    </td>
                    <td>{formatDate(exec.started_at)}</td>
                    <td>{formatDuration(exec.duration_seconds)}</td>
                    <td>
                      <div className="progress-info">
                        {exec.items_completed}/{exec.total_items}
                        {exec.items_failed > 0 && (
                          <span className="failed-count">({exec.items_failed} failed)</span>
                        )}
                      </div>
                    </td>
                    <td>{exec.triggered_by_username || 'System'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Item to Profile</h2>
              <button onClick={() => setShowAddModal(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleAddItem}>
              <div className="form-group">
                <label>Item Type</label>
                <div className="type-selector">
                  <button
                    type="button"
                    className={`type-btn ${selectedItemType === 'flow' ? 'active' : ''}`}
                    onClick={() => { setSelectedItemType('flow'); setSelectedItemId(''); }}
                  >
                    Flow
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${selectedItemType === 'pipeline' ? 'active' : ''}`}
                    onClick={() => { setSelectedItemType('pipeline'); setSelectedItemId(''); }}
                  >
                    Pipeline
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Select {selectedItemType === 'flow' ? 'Flow' : 'Pipeline'}</label>
                <select
                  value={selectedItemId}
                  onChange={(e) => setSelectedItemId(e.target.value)}
                  required
                >
                  <option value="">-- Select --</option>
                  {selectedItemType === 'flow'
                    ? filteredItems.flows.map((flow) => (
                        <option key={flow.id} value={flow.id}>
                          {flow.name} ({flow.odoo_model})
                        </option>
                      ))
                    : filteredItems.pipelines.map((pipeline) => (
                        <option key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </option>
                      ))
                  }
                </select>
                {selectedItemType === 'flow' && filteredItems.flows.length === 0 && (
                  <p className="no-items-hint">All flows are already in this profile.</p>
                )}
                {selectedItemType === 'pipeline' && filteredItems.pipelines.length === 0 && (
                  <p className="no-items-hint">All pipelines are already in this profile.</p>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={!selectedItemId}>
                  Add Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunProfileDetail;
